import {
  gestionesOf,
  isPendiente,
  montoPagado,
  saldoPendiente,
} from './cobranzas';
import { formatMoney } from './money';
import { scoresFromInvoices } from './collectionScoreCompute';
import { compareCobrarPriority, computeCobrarPriority } from './cobrarPriority';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(iso, days) {
  const [y, m, d] = String(iso).split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function latestPromesa(factura) {
  let latest = null;
  for (const g of gestionesOf(factura)) {
    if (g.tipo !== 'promesa' || !g.promesaFecha) continue;
    const fecha = String(g.promesaFecha).slice(0, 10);
    if (!latest || fecha > latest) latest = fecha;
  }
  return latest;
}

function cobrarHref(factura) {
  const cliente = String(factura?.razonSocial || '').trim();
  if (!cliente) return '/cobranzas';
  return `/cobranzas?q=${encodeURIComponent(cliente)}`;
}

export function buildCobrarHoy({ invoices = [], limit = 5, profesional = false } = {}) {
  const today = todayISO();
  const pendientes = invoices.filter(isPendiente);
  const scoreMap = profesional ? scoresFromInvoices(invoices) : {};
  const tasks = [];

  for (const factura of pendientes) {
    const cliente = factura.razonSocial || factura.cliente || 'Sin cliente';
    const scored = computeCobrarPriority(factura, {
      profesional,
      clientScore: scoreMap[cliente]?.score ?? null,
      today,
    });
    if (!scored) continue;

    tasks.push({
      id: `cobrar-${factura._id || factura.id || `${factura.numero}-${factura.fecha}`}`,
      kind: 'cobrar',
      title: cliente,
      detail: `${scored.reason} · ${formatMoney(scored.saldo, scored.moneda)}`,
      meta: factura.numero ? `Registro #${factura.numero}` : null,
      href: cobrarHref(factura),
      tone: scored.tone,
      priority: scored.priority,
      priorityLabel: scored.priorityLabel,
      saldo: scored.saldo,
      moneda: scored.moneda,
      clientScore: scored.clientScore,
      partial: scored.partial,
    });
  }

  tasks.sort(compareCobrarPriority);
  const rankedTasks = tasks.map((task, index) => ({
    ...task,
    priorityRank: index + 1,
  }));

  const totalArs = pendientes
    .filter((f) => (f.moneda || 'ARS') === 'ARS')
    .reduce((sum, f) => sum + saldoPendiente(f), 0);

  return {
    tasks: rankedTasks.slice(0, limit),
    totalPendientes: pendientes.length,
    totalArs: Math.round(totalArs * 100) / 100,
    hasMore: rankedTasks.length > limit,
    smartPriority: profesional,
  };
}

export function buildBandeja({
  invoices = [],
  fiscal = null,
  sinCotizacion = 0,
  lowScoreClients = [],
  atRiskClients = [],
  fiscalChecklist = null,
  hasCobrarHoy = false,
} = {}) {
  const items = [];

  if (sinCotizacion > 0) {
    items.push({
      id: 'fx',
      title: `${sinCotizacion} registro${sinCotizacion === 1 ? '' : 's'} sin cotización`,
      detail: 'No entran en el consolidado en pesos hasta que tengan tipo de cambio.',
      href: '/facturas',
      tone: 'amber',
    });
  }

  if (atRiskClients.length > 0) {
    const top = atRiskClients[0];
    const names = atRiskClients.slice(0, 2).map((c) => c.cliente).join(', ');
    items.push({
      id: 'score-caida',
      title: atRiskClients.length === 1
        ? 'Cliente con score en caída'
        : `${atRiskClients.length} clientes con score en caída`,
      detail: top?.reason
        ? `${names} — ${top.reason}`
        : (names || 'Revisá Cobrar'),
      href: '/cobranzas',
      tone: 'red',
    });
  } else if (lowScoreClients.length > 0) {
    const names = lowScoreClients.slice(0, 2).map((c) => c.cliente).join(', ');
    items.push({
      id: 'score-bajo',
      title: lowScoreClients.length === 1
        ? '1 cliente con score de cobranza bajo'
        : `${lowScoreClients.length} clientes con score bajo`,
      detail: names ? `${names} — revisá cobranzas` : 'Revisá el ranking en Clientes',
      href: '/clientes-detalle',
      tone: 'amber',
    });
  }

  if (fiscalChecklist && fiscalChecklist.total > fiscalChecklist.completed) {
    const pending = fiscalChecklist.total - fiscalChecklist.completed;
    items.push({
      id: 'checklist-fiscal',
      title: pending === 1 ? '1 tarea fiscal pendiente' : `${pending} tareas fiscales pendientes`,
      detail: 'Checklist del mes en Cuenta → Fiscal',
      href: '/cuenta?tab=fiscal',
      tone: 'amber',
    });
  }

  for (const ev of fiscal?.proximosCalendario || []) {
    items.push({
      id: `fiscal-cal-${ev.id}`,
      title: ev.titulo,
      detail: ev.descripcion || `Vence el ${ev.fecha}`,
      href: '/cuenta?tab=fiscal',
      tone: ev.diasRestantes <= 1 ? 'red' : 'amber',
    });
  }

  if (fiscal?.regimen === 'monotributo' && (fiscal.porcentajeTope || 0) >= 80) {
    const pct = Math.round(fiscal.porcentajeTope || 0);
    items.push({
      id: 'fiscal',
      title: `Monotributo al ${pct}% del tope`,
      detail: pct >= 100 ? 'Revisá categoría u orientación fiscal.' : 'Vas cerca del tope de la categoría.',
      href: '/reportes?tab=pyl',
      tone: pct >= 100 ? 'red' : 'amber',
    });
  } else if (
    fiscal?.regimen === 'monotributo'
    && fiscal.mesesHastaTope != null
    && fiscal.mesesHastaTope <= 4
  ) {
    items.push({
      id: 'runrate',
      title: fiscal.yaSuperoTope
        ? 'Ya superaste el tope'
        : `Tope estimado: ${fiscal.mesEstimadoTope || 'próximos meses'}`,
      detail: 'A este ritmo (últimos 90 días). Orientativo; no es una liquidación.',
      href: '/reportes?tab=pyl',
      tone: fiscal.mesesHastaTope <= 2 ? 'red' : 'amber',
    });
  }

  const pendientes = invoices.filter(isPendiente);
  const parciales = pendientes.filter((f) => montoPagado(f) > 0.009);
  if (parciales.length && !hasCobrarHoy) {
    items.push({
      id: 'parciales',
      title: parciales.length === 1 ? '1 venta con saldo parcial' : `${parciales.length} ventas con saldo parcial`,
      detail: 'El pendiente ya no es todo o nada: queda un resto por cobrar.',
      href: '/cobranzas',
      tone: 'amber',
    });
  }

  const today = todayISO();
  const weekEnd = addDaysISO(today, 7);
  const promesasSemana = [];
  for (const factura of pendientes) {
    const promesaFecha = latestPromesa(factura);
    if (!promesaFecha || promesaFecha > weekEnd || promesaFecha < today) continue;
    promesasSemana.push(factura);
  }
  if (promesasSemana.length && !hasCobrarHoy) {
    items.push({
      id: 'promesas-semana',
      title: `${promesasSemana.length} promesa${promesasSemana.length === 1 ? '' : 's'} esta semana`,
      detail: 'Revisá el seguimiento en Cobrar.',
      href: '/cobranzas',
      tone: 'amber',
    });
  }

  return items.slice(0, 5);
}
